/**
 * Tabelas das abas de Ordens de serviço, Peças, Serviços, Colaboradores e
 * Estoque. Recebem as linhas JÁ filtradas (a página é dona do recorte, que
 * também alimenta o CSV/PDF) e só cuidam de exibir + paginar.
 *
 * Abaixo de 1100px as tabelas viram cards (`table--responsive` +
 * `data-label`).
 */
import type { OrcamentoResumo } from '@/features/orcamentos/api/orcamentosApi';
import type { EtapaKanban } from '@/features/orcamentos/api/etapasKanban';
import type { UsoDetalhado } from '@/features/relatorios/api/relatoriosApi';
import type { ProdutoResumo } from '@/features/produtos/api/produtosApi';
import type { MovimentacaoEstoqueResumo } from '@/features/estoque/api/estoqueApi';
import { usePaginacao } from '@/shared/hooks/usePaginacao';
import { Paginacao } from '@/shared/ui/Paginacao';
import {
  ROTULO_ORIGEM,
  ROTULO_SITUACAO_ESTOQUE,
  SEM_COLABORADOR,
  SEM_RESPONSAVEL,
  estaAtrasada,
  rankingUsos,
  situacaoSaldo,
  type ResumoColaborador,
} from '@/features/relatorios/filtrosRelatorio';
import type {
  FiltrosAbaColaboradores,
  FiltrosAbaEstoque,
  FiltrosAbaItens,
  FiltrosAbaOrcamentos,
} from '@/features/relatorios/filtrosAtivos';
import {
  AlternanciaVisao,
  CheckboxFiltro,
  SecaoTabela,
  SelectBaseData,
  SelectFiltro,
  SelectStatusOS,
  useFormatoMoeda,
} from '@/features/relatorios/components/FiltrosAba';

const fmtData = new Intl.DateTimeFormat('pt-BR');
const dataCurta = (iso: string) => fmtData.format(new Date(iso));
const formatarQuantidade = (valor: number) => Math.trunc(valor).toLocaleString('pt-BR');
const TAMANHO_PAGINA = 25;

function PaginacaoDe<T>({ p }: { p: ReturnType<typeof usePaginacao<T>> }) {
  return (
    <Paginacao
      pagina={p.pagina}
      totalPaginas={p.totalPaginas}
      total={p.total}
      tamanhoPagina={p.tamanhoPagina}
      onMudarPagina={p.setPagina}
    />
  );
}

// ---------------------------------------------------------------------------
// Ordens de serviço
// ---------------------------------------------------------------------------

export function TabelaOrdensServico({
  linhas,
  totalNoPeriodo,
  filtros: f,
  aplicar,
  etapas,
  responsaveis,
  nomeCliente,
  rotuloDe,
}: {
  linhas: OrcamentoResumo[];
  totalNoPeriodo: number;
  filtros: FiltrosAbaOrcamentos;
  aplicar: (p: Partial<FiltrosAbaOrcamentos>) => void;
  etapas: EtapaKanban[];
  responsaveis: Array<{ id: string; nome: string }>;
  nomeCliente: (id: string) => string;
  rotuloDe: (id: string) => string;
}) {
  const fmtMoeda = useFormatoMoeda();
  const pag = usePaginacao(linhas, TAMANHO_PAGINA);
  const agora = new Date();
  const totalItens = linhas.reduce((acc, o) => acc + o.quantidadeItens, 0);
  const totalValor = linhas.reduce((acc, o) => acc + o.total, 0);

  return (
    <SecaoTabela
      titulo={`Ordens de serviço no período (${linhas.length} de ${totalNoPeriodo})`}
      busca={f.busca}
      onBusca={(busca) => aplicar({ busca })}
      placeholder="Buscar por número, placa, cliente ou status…"
      filtroExtra={
        <>
          <SelectBaseData valor={f.baseData} onChange={(baseData) => aplicar({ baseData })} />
          <SelectStatusOS
            rotulo="Status"
            valor={f.status}
            onChange={(status) => aplicar({ status })}
            etapas={etapas}
          />
          <SelectFiltro
            rotulo="Responsável"
            valor={f.responsavel}
            onChange={(responsavel) => aplicar({ responsavel })}
          >
            <option value="">Todos</option>
            <option value={SEM_RESPONSAVEL}>Sem responsável</option>
            {responsaveis.map((r) => (
              <option key={r.id} value={r.id}>
                {r.nome}
              </option>
            ))}
          </SelectFiltro>
          <SelectFiltro
            rotulo="Prazo de entrega"
            valor={f.prazo}
            onChange={(prazo) => aplicar({ prazo: prazo as FiltrosAbaOrcamentos['prazo'] })}
          >
            <option value="">Todas</option>
            <option value="atrasadas">Atrasadas</option>
            <option value="no-prazo">No prazo</option>
          </SelectFiltro>
        </>
      }
    >
      {linhas.length === 0 ? (
        <p className="empty-state">Nenhuma ordem de serviço encontrada.</p>
      ) : (
        <div className="table-scroll">
          <table className="table table--responsive">
            <thead>
              <tr>
                <th>Número</th>
                <th>Data</th>
                <th>Cliente</th>
                <th>Placa</th>
                <th>Responsável</th>
                <th className="table__numeric">KM</th>
                <th>Status</th>
                <th className="table__numeric">Itens</th>
                <th className="table__numeric">Total</th>
              </tr>
            </thead>
            <tbody>
              {pag.paginados.map((o) => {
                const atrasada = estaAtrasada(o, agora);
                return (
                  <tr key={o.id} className={atrasada ? 'relatorio-linha--alerta' : undefined}>
                    <td data-label="Número" className="table__primary">{o.numero}</td>
                    <td data-label="Data" className="table__muted">{dataCurta(o.data)}</td>
                    <td data-label="Cliente">{nomeCliente(o.clienteId)}</td>
                    <td data-label="Placa" className="table__muted">{o.veiculoDescricao}</td>
                    <td data-label="Responsável" className="table__muted">
                      {o.colaboradorResponsavelNome ?? '—'}
                    </td>
                    <td data-label="KM" className="table__numeric table__muted">
                      {o.kmRegistrado != null ? o.kmRegistrado.toLocaleString('pt-BR') : '—'}
                    </td>
                    <td data-label="Status">
                      <span className="badge">{rotuloDe(o.status)}</span>
                      {atrasada && (
                        <>
                          {' '}
                          <span className="badge badge--saida">Atrasada</span>
                        </>
                      )}
                    </td>
                    <td data-label="Itens" className="table__numeric">{o.quantidadeItens}</td>
                    <td data-label="Total" className="table__numeric">{fmtMoeda(o.total)}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={7} className="table__primary">Totais</td>
                <td data-label="Itens" className="table__numeric table__primary">
                  {totalItens.toLocaleString('pt-BR')}
                </td>
                <td data-label="Total" className="table__numeric table__primary">
                  {fmtMoeda(totalValor)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
      <PaginacaoDe p={pag} />
    </SecaoTabela>
  );
}

// ---------------------------------------------------------------------------
// Peças / Serviços
// ---------------------------------------------------------------------------

export function TabelaUsos({
  titulo,
  rotuloItem,
  rotuloTodos,
  placeholder,
  linhas,
  totalSemFiltro,
  filtros: f,
  aplicar,
  etapas,
  itens,
  colaboradores,
}: {
  titulo: string;
  rotuloItem: string;
  /** "Todas as peças" / "Todos os serviços". */
  rotuloTodos: string;
  placeholder: string;
  linhas: UsoDetalhado[];
  totalSemFiltro: number;
  filtros: FiltrosAbaItens;
  aplicar: (p: Partial<FiltrosAbaItens>) => void;
  etapas: EtapaKanban[];
  /** Itens para o select, já recortados pelo status escolhido. */
  itens: Array<{ id: string; descricao: string }>;
  colaboradores: Array<{ id: string; nome: string }>;
}) {
  const ranking = f.visao === 'ranking';
  return (
    <SecaoTabela
      titulo={`${titulo} (${linhas.length} de ${totalSemFiltro})`}
      busca={f.busca}
      onBusca={(busca) => aplicar({ busca })}
      placeholder={placeholder}
      acoesTitulo={<AlternanciaVisao valor={f.visao} onChange={(visao) => aplicar({ visao })} />}
      filtroExtra={
        <>
          <SelectStatusOS
            valor={f.status}
            onChange={(status) => aplicar({ status })}
            etapas={etapas}
          />
          <SelectFiltro
            rotulo={rotuloItem}
            valor={f.referenciaId}
            onChange={(referenciaId) => aplicar({ referenciaId })}
          >
            <option value="">{rotuloTodos}</option>
            {itens.map((p) => (
              <option key={p.id} value={p.id}>
                {p.descricao}
              </option>
            ))}
          </SelectFiltro>
          <SelectFiltro
            rotulo="Colaborador"
            valor={f.colaborador}
            onChange={(colaborador) => aplicar({ colaborador })}
          >
            <option value="">Todos</option>
            <option value={SEM_COLABORADOR}>Sem colaborador</option>
            {colaboradores.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nome}
              </option>
            ))}
          </SelectFiltro>
          <CheckboxFiltro
            rotulo="Somente com desconto"
            marcado={f.somenteComDesconto}
            onChange={(somenteComDesconto) => aplicar({ somenteComDesconto })}
          />
        </>
      }
    >
      {linhas.length === 0 ? (
        <p className="empty-state">Nenhum registro no período.</p>
      ) : ranking ? (
        <RankingUsos linhas={linhas} rotuloItem={rotuloItem} />
      ) : (
        <DetalheUsos linhas={linhas} rotuloItem={rotuloItem} />
      )}
    </SecaoTabela>
  );
}

function DetalheUsos({ linhas, rotuloItem }: { linhas: UsoDetalhado[]; rotuloItem: string }) {
  const fmtMoeda = useFormatoMoeda();
  const pag = usePaginacao(linhas, TAMANHO_PAGINA);
  const totalQtd = linhas.reduce((acc, l) => acc + l.quantidade, 0);
  const totalDesconto = linhas.reduce((acc, l) => acc + l.valorDesconto, 0);
  const totalValor = linhas.reduce((acc, l) => acc + l.subtotal, 0);
  return (
    <>
      <div className="table-scroll">
        <table className="table table--responsive">
          <thead>
            <tr>
              <th>OS</th>
              <th>Data</th>
              <th>Cliente</th>
              <th>{rotuloItem}</th>
              <th>Colaborador</th>
              <th className="table__numeric">Qtd.</th>
              <th className="table__numeric">Preço unit.</th>
              <th className="table__numeric">Desconto</th>
              <th className="table__numeric">Subtotal</th>
            </tr>
          </thead>
          <tbody>
            {pag.paginados.map((l, idx) => (
              <tr key={`${l.orcamentoId}-${l.referenciaId}-${idx}`}>
                <td data-label="OS" className="table__primary">{l.orcamentoNumero}</td>
                <td data-label="Data" className="table__muted">{dataCurta(l.orcamentoData)}</td>
                <td data-label="Cliente">{l.clienteNome}</td>
                <td data-label={rotuloItem}>{l.descricao}</td>
                <td data-label="Colaborador" className="table__muted">{l.colaboradorNome ?? '—'}</td>
                <td data-label="Qtd." className="table__numeric">{l.quantidade.toLocaleString('pt-BR')}</td>
                <td data-label="Preço unit." className="table__numeric">{fmtMoeda(l.precoUnitario)}</td>
                <td data-label="Desconto" className="table__numeric">
                  {l.valorDesconto > 0 ? `− ${fmtMoeda(l.valorDesconto)}` : '—'}
                </td>
                <td data-label="Subtotal" className="table__numeric">{fmtMoeda(l.subtotal)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={5} className="table__primary">Totais</td>
              <td data-label="Qtd." className="table__numeric table__primary">
                {totalQtd.toLocaleString('pt-BR')}
              </td>
              {/* Somar preço unitário não tem significado — a coluna fica vazia. */}
              <td className="table__numeric table__primary" />
              <td data-label="Desconto" className="table__numeric table__primary">
                {totalDesconto > 0 ? `− ${fmtMoeda(totalDesconto)}` : '—'}
              </td>
              <td data-label="Subtotal" className="table__numeric table__primary">
                {fmtMoeda(totalValor)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      <PaginacaoDe p={pag} />
    </>
  );
}

function RankingUsos({ linhas, rotuloItem }: { linhas: UsoDetalhado[]; rotuloItem: string }) {
  const fmtMoeda = useFormatoMoeda();
  const ranking = rankingUsos(linhas);
  const pag = usePaginacao(ranking, TAMANHO_PAGINA);
  const inicio = (pag.pagina - 1) * pag.tamanhoPagina;
  return (
    <>
      <div className="table-scroll">
        <table className="table table--responsive">
          <thead>
            <tr>
              <th className="table__numeric">#</th>
              <th>{rotuloItem}</th>
              <th className="table__numeric">Qtd. total</th>
              <th className="table__numeric">Nº de O.S.</th>
              <th className="table__numeric">Desconto</th>
              <th className="table__numeric">Valor</th>
            </tr>
          </thead>
          <tbody>
            {pag.paginados.map((r, idx) => (
              <tr key={r.referenciaId}>
                <td data-label="#" className="table__numeric table__muted">{inicio + idx + 1}</td>
                <td data-label={rotuloItem} className="table__primary">{r.descricao}</td>
                <td data-label="Qtd. total" className="table__numeric">
                  {r.quantidade.toLocaleString('pt-BR')}
                </td>
                <td data-label="Nº de O.S." className="table__numeric">{r.ordens}</td>
                <td data-label="Desconto" className="table__numeric">
                  {r.desconto > 0 ? `− ${fmtMoeda(r.desconto)}` : '—'}
                </td>
                <td data-label="Valor" className="table__numeric">{fmtMoeda(r.valor)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={2} className="table__primary">
                Totais ({ranking.length} {ranking.length === 1 ? 'item' : 'itens'})
              </td>
              <td data-label="Qtd. total" className="table__numeric table__primary">
                {ranking.reduce((a, r) => a + r.quantidade, 0).toLocaleString('pt-BR')}
              </td>
              <td className="table__numeric table__primary" />
              <td data-label="Desconto" className="table__numeric table__primary">
                {fmtMoeda(ranking.reduce((a, r) => a + r.desconto, 0))}
              </td>
              <td data-label="Valor" className="table__numeric table__primary">
                {fmtMoeda(ranking.reduce((a, r) => a + r.valor, 0))}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      <PaginacaoDe p={pag} />
    </>
  );
}

// ---------------------------------------------------------------------------
// Colaboradores
// ---------------------------------------------------------------------------

export function TabelaColaboradores({
  linhas,
  resumo,
  totalSemFiltro,
  filtros: f,
  aplicar,
  etapas,
  colaboradoresDisponiveis,
}: {
  linhas: UsoDetalhado[];
  resumo: ResumoColaborador[];
  totalSemFiltro: number;
  filtros: FiltrosAbaColaboradores;
  aplicar: (p: Partial<FiltrosAbaColaboradores>) => void;
  etapas: EtapaKanban[];
  colaboradoresDisponiveis: Array<{ id: string; nome: string }>;
}) {
  const fmtMoeda = useFormatoMoeda();
  const pag = usePaginacao(linhas, TAMANHO_PAGINA);
  const totalPecas = linhas.filter((l) => l.tipo === 'Produto').length;
  const totalServicos = linhas.filter((l) => l.tipo === 'Servico').length;
  const totalValor = linhas.reduce((acc, l) => acc + l.subtotal, 0);

  return (
    <>
      <div className="stats-grid" style={{ marginBottom: 16 }}>
        <div className="stat-card">
          <span className="stat-card__label">Colaboradores</span>
          <span className="stat-card__value">{resumo.length}</span>
        </div>
        <div className="stat-card">
          <span className="stat-card__label">Peças responsáveis</span>
          <span className="stat-card__value">{totalPecas}</span>
        </div>
        <div className="stat-card">
          <span className="stat-card__label">Serviços responsáveis</span>
          <span className="stat-card__value">{totalServicos}</span>
        </div>
        <div className="stat-card">
          <span className="stat-card__label">Valor vinculado</span>
          <span className="stat-card__value">{fmtMoeda(totalValor)}</span>
        </div>
      </div>

      <SecaoTabela
        titulo={`Itens por colaborador (${linhas.length} de ${totalSemFiltro})`}
        busca={f.busca}
        onBusca={(busca) => aplicar({ busca })}
        placeholder="Buscar por colaborador, item, ordem de serviço ou cliente..."
        filtroExtra={
          <>
            <SelectBaseData valor={f.baseData} onChange={(baseData) => aplicar({ baseData })} />
            <SelectStatusOS
              valor={f.status}
              onChange={(status) => aplicar({ status })}
              etapas={etapas}
            />
            <SelectFiltro
              rotulo="Colaborador"
              valor={f.colaborador}
              onChange={(colaborador) => aplicar({ colaborador })}
            >
              <option value="">Todos os colaboradores</option>
              {colaboradoresDisponiveis.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nome}
                </option>
              ))}
            </SelectFiltro>
            <SelectFiltro
              rotulo="Tipo"
              valor={f.tipo}
              onChange={(tipo) => aplicar({ tipo: tipo as FiltrosAbaColaboradores['tipo'] })}
            >
              <option value="">Peças e serviços</option>
              <option value="Produto">Peças</option>
              <option value="Servico">Serviços</option>
            </SelectFiltro>
          </>
        }
      >
        {linhas.length === 0 ? (
          <p className="empty-state">Nenhum item com colaborador responsável no período.</p>
        ) : (
          <div className="table-scroll">
            <table className="table table--responsive">
              <thead>
                <tr>
                  <th>Colaborador</th>
                  <th>Tipo</th>
                  <th>Item</th>
                  <th>OS</th>
                  <th>Data</th>
                  <th>Cliente</th>
                  <th className="table__numeric">Qtd.</th>
                  <th className="table__numeric">Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {pag.paginados.map((l, idx) => (
                  <tr key={`${l.orcamentoId}-${l.referenciaId}-${l.colaboradorId}-${idx}`}>
                    <td data-label="Colaborador" className="table__primary">{l.colaboradorNome}</td>
                    <td data-label="Tipo">
                      <span className={`badge ${l.tipo === 'Produto' ? 'badge--neutro' : 'badge--ativo'}`}>
                        {l.tipo === 'Produto' ? 'Peça' : 'Serviço'}
                      </span>
                    </td>
                    <td data-label="Item">{l.descricao}</td>
                    <td data-label="OS" className="table__primary">{l.orcamentoNumero}</td>
                    <td data-label="Data" className="table__muted">{dataCurta(l.orcamentoData)}</td>
                    <td data-label="Cliente">{l.clienteNome}</td>
                    <td data-label="Qtd." className="table__numeric">{l.quantidade.toLocaleString('pt-BR')}</td>
                    <td data-label="Subtotal" className="table__numeric">{fmtMoeda(l.subtotal)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <PaginacaoDe p={pag} />
      </SecaoTabela>
    </>
  );
}

// ---------------------------------------------------------------------------
// Estoque
// ---------------------------------------------------------------------------

export type TotaisEstoque = {
  saldoAtual: number;
  produtosComEstoque: number;
  produtosSemEstoque: number;
  produtosSaldoNegativo: number;
  entradas: number;
  saidas: number;
};

const BADGE_SITUACAO: Record<'com' | 'sem' | 'negativo', string> = {
  com: 'badge--ativo',
  sem: 'badge--sem-estoque',
  negativo: 'badge--saida',
};

export function TabelaEstoque({
  produtos,
  totalProdutos,
  movimentacoes,
  totais,
  filtros: f,
  aplicar,
}: {
  produtos: ProdutoResumo[];
  totalProdutos: number;
  movimentacoes: MovimentacaoEstoqueResumo[];
  totais: TotaisEstoque;
  filtros: FiltrosAbaEstoque;
  aplicar: (p: Partial<FiltrosAbaEstoque>) => void;
}) {
  const produtosPaginados = usePaginacao(produtos, TAMANHO_PAGINA);
  const movimentacoesPaginadas = usePaginacao(movimentacoes, TAMANHO_PAGINA);
  return (
    <div className="relatorio-estoque">
      <div className="stats-grid relatorio-estoque__stats">
        <div className="stat-card">
          <span className="stat-card__label">Unidades em estoque</span>
          <span className="stat-card__value">{formatarQuantidade(totais.saldoAtual)}</span>
        </div>
        <div className="stat-card">
          <span className="stat-card__label">Produtos com estoque</span>
          <span className="stat-card__value">{totais.produtosComEstoque}</span>
        </div>
        <div className="stat-card">
          <span className="stat-card__label">Produtos sem estoque</span>
          <span className="stat-card__value">{totais.produtosSemEstoque}</span>
        </div>
        {totais.produtosSaldoNegativo > 0 && (
          <div className="stat-card">
            <span className="stat-card__label">Saldo negativo</span>
            <span className="stat-card__value stat-card__value--danger">
              {totais.produtosSaldoNegativo}
            </span>
          </div>
        )}
        <div className="stat-card">
          <span className="stat-card__label">Entradas no período</span>
          <span className="stat-card__value stat-card__value--success">
            +{formatarQuantidade(totais.entradas)}
          </span>
        </div>
        <div className="stat-card">
          <span className="stat-card__label">Saídas no período</span>
          <span className="stat-card__value stat-card__value--danger">
            -{formatarQuantidade(totais.saidas)}
          </span>
        </div>
      </div>

      <SecaoTabela
        titulo={`Posição de estoque (${produtos.length} de ${totalProdutos})`}
        busca={f.busca}
        onBusca={(busca) => aplicar({ busca })}
        placeholder="Buscar código, produto ou motivo..."
        filtroExtra={
          <>
            <SelectFiltro
              rotulo="Situação"
              valor={f.situacao}
              onChange={(v) => aplicar({ situacao: v as FiltrosAbaEstoque['situacao'] })}
            >
              <option value="">Todos os produtos</option>
              <option value="com">Com estoque</option>
              <option value="sem">Sem estoque</option>
              <option value="negativo">Saldo negativo</option>
            </SelectFiltro>
            <SelectFiltro
              rotulo="Cadastro"
              valor={f.cadastro}
              onChange={(v) => aplicar({ cadastro: v as FiltrosAbaEstoque['cadastro'] })}
            >
              <option value="ativos">Ativos</option>
              <option value="inativos">Inativos</option>
              <option value="todos">Ativos e inativos</option>
            </SelectFiltro>
            <SelectFiltro
              rotulo="Movimento"
              valor={f.tipoMovimento}
              onChange={(v) =>
                aplicar({ tipoMovimento: v as FiltrosAbaEstoque['tipoMovimento'] })
              }
            >
              <option value="">Entradas e saídas</option>
              <option value="Entrada">Entradas</option>
              <option value="Saida">Saídas</option>
            </SelectFiltro>
            <SelectFiltro
              rotulo="Origem do movimento"
              valor={f.origem}
              onChange={(v) => aplicar({ origem: v as FiltrosAbaEstoque['origem'] })}
            >
              <option value="">Todas as origens</option>
              <option value="Orcamento">{ROTULO_ORIGEM.Orcamento}</option>
              <option value="Estoque">{ROTULO_ORIGEM.Estoque}</option>
              <option value="EstornoOrcamento">{ROTULO_ORIGEM.EstornoOrcamento}</option>
              <option value="Produto">{ROTULO_ORIGEM.Produto}</option>
            </SelectFiltro>
            <CheckboxFiltro
              rotulo="Ocultar movimentações estornadas"
              marcado={f.ocultarEstornadas}
              onChange={(ocultarEstornadas) => aplicar({ ocultarEstornadas })}
            />
          </>
        }
      >
        {produtos.length === 0 ? (
          <p className="empty-state">Nenhum produto encontrado.</p>
        ) : (
          <div className="table-scroll">
            <table className="table table--responsive">
              <thead>
                <tr>
                  <th>Produto</th>
                  <th className="table__numeric">Estoque atual</th>
                  <th>Situação</th>
                  <th>Cadastro</th>
                </tr>
              </thead>
              <tbody>
                {produtosPaginados.paginados.map((p) => {
                  // Saldo real: negativo aparece negativo (é o sinal do problema).
                  const saldo = p.estoqueQuantidade ?? 0;
                  const situacao = situacaoSaldo(saldo);
                  return (
                    <tr
                      key={p.id}
                      className={situacao === 'negativo' ? 'relatorio-linha--alerta' : undefined}
                    >
                      <td data-label="Produto">
                        <div className="relatorio-estoque__produto">
                          <span className="table__primary">{p.nome}</span>
                          <span className="table__muted">{p.codigo}</span>
                        </div>
                      </td>
                      <td
                        data-label="Estoque atual"
                        className={`table__numeric${situacao === 'negativo' ? ' relatorio-valor--negativo' : ''}`}
                      >
                        {formatarQuantidade(saldo)}
                      </td>
                      <td data-label="Situação">
                        <span className={`badge ${BADGE_SITUACAO[situacao]}`}>
                          {ROTULO_SITUACAO_ESTOQUE[situacao]}
                        </span>
                      </td>
                      <td data-label="Cadastro">
                        {p.ativo ? (
                          <span className="badge badge--neutro">Ativo</span>
                        ) : (
                          <span className="badge badge--inativo">Inativo</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <PaginacaoDe p={produtosPaginados} />
      </SecaoTabela>

      <div className="card relatorio-estoque__movimentos">
        <div className="card__header">
          <div>
            <span className="card__title">Movimentações no período ({movimentacoes.length})</span>
            <span className="relatorio-estoque__descricao">
              Entradas e saídas filtradas pelo período e pelos filtros acima.
            </span>
          </div>
        </div>
        <div className="card__body card__body--flush">
          {movimentacoes.length === 0 ? (
            <p className="empty-state">Nenhuma movimentação no período.</p>
          ) : (
            <div className="table-scroll">
              <table className="table table--responsive">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Produto</th>
                    <th>Movimento</th>
                    <th>Origem</th>
                    <th className="table__numeric">Qtd.</th>
                    <th className="table__numeric">Saldo</th>
                    <th>Motivo</th>
                  </tr>
                </thead>
                <tbody>
                  {movimentacoesPaginadas.paginados.map((m) => (
                    <tr key={m.id}>
                      <td data-label="Data" className="table__muted">{dataCurta(m.criadoEm)}</td>
                      <td data-label="Produto">
                        <div className="relatorio-estoque__produto">
                          <span className="table__primary">{m.produtoNome}</span>
                          <span className="table__muted">{m.produtoCodigo}</span>
                        </div>
                      </td>
                      <td data-label="Movimento">
                        <span className={`badge ${m.tipo === 'Entrada' ? 'badge--entrada' : 'badge--saida'}`}>
                          {m.tipo === 'Entrada' ? 'Entrada' : 'Saída'}
                        </span>
                      </td>
                      <td data-label="Origem" className="table__muted">
                        {ROTULO_ORIGEM[m.origemTipo] ?? m.origemTipo}
                        {m.estornada ? ' · estornada' : ''}
                      </td>
                      <td
                        data-label="Qtd."
                        className={`table__numeric relatorio-estoque__qtd ${m.tipo === 'Entrada' ? 'relatorio-estoque__qtd--entrada' : 'relatorio-estoque__qtd--saida'}`}
                      >
                        {m.tipo === 'Entrada' ? '+' : '-'}{formatarQuantidade(m.quantidade)}
                      </td>
                      <td data-label="Saldo" className="table__numeric table__muted">
                        {formatarQuantidade(m.saldoAntes)} -&gt; {formatarQuantidade(m.saldoDepois)}
                      </td>
                      <td data-label="Motivo">{m.motivo}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <PaginacaoDe p={movimentacoesPaginadas} />
        </div>
      </div>
    </div>
  );
}
